/**
 * The reporting range every screen is scoped to.
 *
 * The options come from the API, not from a hardcoded list here: a calendar
 * year is offerable only once it has a closed month, and the server is the
 * only thing that knows that.
 */
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export interface RangeOption {
  key: string;
  label: string;
}

export interface RangeInfo {
  key: string;
  label: string;
  periods: string[];
  partial: boolean;
  note: string | null;
  closed_only: boolean;
  available: RangeOption[];
}

export function RangeSelect({
  range,
  value,
  onChange,
  showCaption = true,
}: {
  range: RangeInfo | undefined;
  value: string;
  onChange: (key: string) => void;
  /** The app header has no room for the "September 2025 to August 2026" line;
   *  a page that shows the selector inline still wants it. */
  showCaption?: boolean;
}) {
  const options = range?.available ?? [];
  // Blank until the options arrive, otherwise. Radix matches `value` against
  // the items it has, and before the first response it has none -- so the
  // trigger rendered as an empty box in the app header on every page load,
  // which reads as broken rather than as pending. Handing it "" surfaces the
  // placeholder instead, and disabling it stops a click opening an empty menu.
  const pending = options.length === 0;
  return (
    // Caption BELOW the control, not beside it. Beside it, the long
    // "September 2025 to August 2026 - closed periods only" line widened the
    // filter group until the selects were dragged back toward the page title,
    // which read as left-aligned filters however the row was justified.
    <div className="flex flex-col items-start gap-1 sm:items-end">
      <Select value={pending ? "" : value} onValueChange={onChange} disabled={pending}>
        <SelectTrigger className="w-[190px]" aria-label="Reporting range">
          <SelectValue placeholder={pending ? "Loading ranges" : "Last 12 months"} />
        </SelectTrigger>
        <SelectContent>
          {options.map((option) => (
            <SelectItem key={option.key} value={option.key}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {range && showCaption ? <RangeCaption range={range} /> : null}
    </div>
  );
}

/**
 * States which months the figures cover.
 *
 * A total with no stated window invites the reader to assume all time, or this
 * month. The partial note matters most: 2026 closed through August is $8.9M
 * against $9.5M for all of 2025 -- without the caveat that reads as a down year
 * rather than eight months outpacing twelve.
 */
/**
 * The caveat ALONE, with no restatement of the range.
 *
 * `RangeCaption` spells out the span and "closed periods only" under the
 * control. Under one control on one page that reads as a caption; on all ten
 * pages it reads as clutter repeating the dropdown, which is what it is -- the
 * dropdown already says "Last 12 months".
 *
 * What is NOT restatement is `range.note` on a partial range. 2026 closed
 * through August is $8.9M against $9.5M for all of 2025: without that caveat
 * the reader sees a down year instead of eight months outpacing twelve, and
 * nothing else on the page says otherwise. So the header carries the warning
 * and drops the description -- it renders nothing at all on an ordinary range,
 * and the full span is on the control's own tooltip for anyone who wants it.
 */
export function RangeWarning({ range }: { range: RangeInfo }) {
  if (!range.partial || !range.note) return null;
  return <p className="text-xs font-medium text-foreground">{range.note}</p>;
}

/** The span a range covers, for a tooltip. Same wording as RangeCaption. */
export function rangeSpan(range: RangeInfo): string {
  const months = range.periods.length;
  if (months === 0) return "no closed months";
  if (months === 1) return range.periods[0] ?? "";
  return `${range.periods[months - 1]} to ${range.periods[0]}`;
}

export function RangeCaption({ range }: { range: RangeInfo }) {
  const months = range.periods.length;
  const span =
    months === 0
      ? "no closed months"
      : months === 1
        ? range.periods[0]
        : `${range.periods[months - 1]} to ${range.periods[0]}`;

  return (
    <p className="text-xs text-muted-foreground">
      {span}
      {range.closed_only ? " · closed periods only" : ""}
      {range.partial && range.note ? (
        <span className="ml-1 font-medium text-foreground">{range.note}</span>
      ) : null}
    </p>
  );
}
